import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertCircle, AlertTriangle, CheckCircle, XCircle,
  ChevronDown, ChevronUp, Trash2, BookOpen, Layers, UserRound,
} from 'lucide-react';
import './RyanAdminErrors.css';

interface ErrorSnapshot {
  deckName: string;
  subdeckName: string;
  cardFront: string;
  cardBack: string;
}

// Populado pela API (GET /api/ryan/admin/errors faz .populate('userId', 'username email'))
// — pode vir null se o usuário que reportou foi deletado depois.
interface ReportedByUser {
  _id: string;
  username?: string;
  email?: string;
}

interface ErrorReport {
  _id: string;
  type: string;
  description: string;
  status: 'pending' | 'accepted' | 'denied';
  cardSnapshot?: ErrorSnapshot;
  createdAt: string;
  userId?: ReportedByUser | null;
}

interface Props {
  onErrorCountChange: (count: number) => void;
  onHeaderChange: (title: string, subtitle: string) => void;
}

const tok = () => localStorage.getItem('accessToken') || '';

function getStatusIcon(status: string) {
  if (status === 'pending') return <AlertCircle size={14} />;
  if (status === 'accepted') return <CheckCircle size={14} />;
  return <XCircle size={14} />;
}

function getStatusLabel(status: string) {
  return { pending: 'Pendente', accepted: 'Aceito', denied: 'Negado' }[status] ?? status;
}

function stripHtml(html: string) {
  const d = document.createElement('div');
  d.innerHTML = html;
  return d.textContent || d.innerText || '';
}

const RyanAdminErrors: React.FC<Props> = ({ onErrorCountChange, onHeaderChange }) => {
  const [errors, setErrors] = useState<ErrorReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'denied'>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    onHeaderChange('Erros Reportados', 'Gerencie os reportes de erros enviados pelos usuários.');
  }, [onHeaderChange]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/ryan/admin/errors', {
        headers: { Authorization: `Bearer ${tok()}` },
      });
      if (res.ok) {
        const data: ErrorReport[] = await res.json();
        setErrors(data);
        onErrorCountChange(data.filter(e => e.status === 'pending').length);
      }
    } finally {
      setLoading(false);
    }
  }, [onErrorCountChange]);

  useEffect(() => { load(); }, [load]);

  const handleStatus = async (id: string, status: string) => {
    await fetch(`/api/ryan/admin/errors/${id}/status`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    load();
  };

  const handleDelete = async (id: string) => {
    await fetch(`/api/ryan/admin/errors/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${tok()}` },
    });
    setDeleteConfirm(null);
    load();
  };

  const filtered = errors.filter(e => filter === 'all' || e.status === filter);

  return (
    <div className="rae-root">
      <div className="rae-header">
        <div className="rae-filter-pills">
          {(['all', 'pending', 'accepted', 'denied'] as const).map(f => (
            <button
              key={f}
              className={`rae-pill${filter === f ? ' rae-pill--active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {{ all: 'Todos', pending: 'Pendentes', accepted: 'Aceitos', denied: 'Negados' }[f]}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="rae-spinner-wrap"><div className="rae-spinner" /></div>}

      {!loading && filtered.length === 0 && (
        <div className="rae-empty">
          <AlertTriangle size={40} color="#0D3B0E" style={{ opacity: 0.25 }} />
          <p>Nenhum erro encontrado.</p>
        </div>
      )}

      {!loading && filtered.map(entry => {
        const snap = entry.cardSnapshot;
        const isExpanded = expanded === entry._id;

        return (
          <div key={entry._id} className={`rae-card rae-card--${entry.status}`}>
            <div className="rae-card-header">
              <strong className="rae-card-type">{entry.type}</strong>
              <span className={`rae-status-badge rae-status-badge--${entry.status}`}>
                {getStatusIcon(entry.status)} {getStatusLabel(entry.status)}
              </span>
              <span className="rae-date">
                {new Date(entry.createdAt).toLocaleDateString('pt-BR')}
              </span>
            </div>

            <div className="rae-reporter">
              <UserRound size={12} />
              {entry.userId
                ? (entry.userId.username || entry.userId.email || `Usuário ${entry.userId._id}`)
                : 'Usuário removido'}
            </div>

            <p className="rae-description">{entry.description}</p>

            {snap && (
              <div className="rae-location">
                <span className="rae-loc-item"><BookOpen size={11} />{snap.deckName}</span>
                <span className="rae-loc-sep">›</span>
                <span className="rae-loc-item"><Layers size={11} />{snap.subdeckName}</span>
              </div>
            )}

            {snap && (snap.cardFront || snap.cardBack) && (
              <div className="rae-preview">
                <button
                  className="rae-btn-expand"
                  onClick={() => setExpanded(isExpanded ? null : entry._id)}
                >
                  {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  {isExpanded ? 'Ocultar card' : 'Ver card'}
                </button>
                {isExpanded && (
                  <div className="rae-card-content">
                    <div className="rae-card-side">
                      <div className="rae-card-side-label">Frente</div>
                      <div className="rae-card-side-text">{stripHtml(snap.cardFront)}</div>
                    </div>
                    <div className="rae-card-side">
                      <div className="rae-card-side-label">Verso</div>
                      <div className="rae-card-side-text">{stripHtml(snap.cardBack)}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="rae-actions">
              {entry.status !== 'accepted' && (
                <button className="rae-btn rae-btn--accept" onClick={() => handleStatus(entry._id, 'accepted')}>
                  <CheckCircle size={13} /> Aceitar
                </button>
              )}
              {entry.status !== 'denied' && (
                <button className="rae-btn rae-btn--deny" onClick={() => handleStatus(entry._id, 'denied')}>
                  <XCircle size={13} /> Negar
                </button>
              )}
              {entry.status !== 'pending' && (
                <button className="rae-btn rae-btn--pending" onClick={() => handleStatus(entry._id, 'pending')}>
                  <AlertCircle size={13} /> Pendente
                </button>
              )}

              {deleteConfirm === entry._id ? (
                <div className="rae-confirm-row">
                  Confirmar exclusão?
                  <button className="rae-confirm-yes" onClick={() => handleDelete(entry._id)}>Sim</button>
                  <button className="rae-confirm-no" onClick={() => setDeleteConfirm(null)}>Não</button>
                </div>
              ) : (
                <button className="rae-btn rae-btn--delete" onClick={() => setDeleteConfirm(entry._id)}>
                  <Trash2 size={13} /> Excluir
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RyanAdminErrors;
